import { Suspense, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router-dom';
import { Spinner } from '../components/ui';
import { useCurrentWorkspace } from '../features/billing/plan';
import { CommandPaletteProvider } from './CommandPalette';
import { MobileTabBar, MobileTopBar } from './MobileNav';
import { SETTINGS_PAGES, isNavActive } from './nav';
import { Sidebar } from './Sidebar';
import { SuspendedScreen, SuspensionGate } from './SuspendedScreen';

/**
 * App chrome (refonte): from md up, the grouped plum sidebar; below it, a top
 * bar, a bottom tab bar and its More sheet. The navigation model lives in
 * `nav.ts`; each screen adapts to its own container width, not this breakpoint.
 *
 * The invoice notice is no longer here: it shows on the Dashboard only, with a
 * dot on Settings everywhere else.
 */
const isInbox = (pathname: string) => pathname === '/inbox' || pathname.startsWith('/inbox/');

/**
 * Settings' pages are one screen, and so is the inbox with a conversation
 * open: moving within them must not replay the page entrance — nor remount the
 * inbox and drop its filters.
 */
function pageKey(pathname: string): string {
  if (isInbox(pathname)) return '/inbox';
  return SETTINGS_PAGES.some((page) => isNavActive(page, pathname)) ? '/settings' : pathname;
}

/** Bottom padding clears the phone tab bar (52px + its padding + safe area). */
const PAGE_COLUMN =
  'max-w-[1240px] animate-page-enter px-4 pb-[calc(88px+env(safe-area-inset-bottom))] pt-5 md:px-8 md:pb-16 md:pt-10 xl:px-12';

/**
 * The inbox takes the whole content area, edge to edge (refonte). On phones
 * that is the viewport less the top bar (54px + its top inset) and the tab bar
 * (59px + its bottom inset).
 */
const FULL_BLEED =
  'animate-page-enter h-[calc(100dvh-113px-max(env(safe-area-inset-top),10px)-max(env(safe-area-inset-bottom),6px))] md:h-dvh';

/**
 * `children` stands in for the routed page: the builder, outside the shell,
 * shows the suspended screen inside it (D-183).
 */
export function AppShell({ children }: { children?: ReactNode } = {}) {
  const { t } = useTranslation();
  const location = useLocation();
  const suspended = Boolean(useCurrentWorkspace().data?.suspendedAt);

  return (
    <CommandPaletteProvider>
      <div className="min-h-dvh md:grid md:grid-cols-[244px_minmax(0,1fr)]">
        <nav aria-label={t('nav.main')} className="hidden bg-[image:var(--gradient-sidebar)] md:block">
          <div className="sticky top-0 h-dvh">
            <Sidebar />
          </div>
        </nav>

        <MobileTopBar />

        {/* min-w-0 is load-bearing: a grid item defaults to min-width:auto, so
            without it any wide child (a table, a segmented control) widens the
            whole column instead of scrolling inside its own card. */}
        <main className="relative min-w-0">
          {/* Suspended, the inbox is the suspended screen too: a page column, not edge to edge. */}
          <div key={pageKey(location.pathname)} className={isInbox(location.pathname) && !suspended ? FULL_BLEED : PAGE_COLUMN}>
            {/* Pages load on demand (route-level code splitting in main.tsx). */}
            <Suspense
              fallback={
                <div className="flex justify-center py-24">
                  <Spinner label="Loading" />
                </div>
              }
            >
              {/* While suspended, every page but Mon offre is the suspended screen (D-183). */}
              <SuspensionGate>{children ?? <Outlet />}</SuspensionGate>
            </Suspense>
          </div>
        </main>

        <MobileTabBar />
      </div>
    </CommandPaletteProvider>
  );
}

/**
 * The builder is a full-screen editor outside the shell. While the workspace
 * is suspended it shows the suspended screen inside the shell instead,
 * navigation included (D-183).
 */
export function BuilderSuspensionGate({ children }: { children: ReactNode }) {
  const workspace = useCurrentWorkspace().data;
  if (workspace?.suspendedAt) {
    return (
      <AppShell>
        <SuspendedScreen workspace={workspace} />
      </AppShell>
    );
  }
  return <>{children}</>;
}
